import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, Users, User, Megaphone } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    recipient_type: "all",
    recipient_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/messages`, { withCredentials: true }),
        axios.get(`${API}/admin/users/list`, { withCredentials: true })
      ]);
      setMessages(messagesRes.data);
      setUsers(usersRes.data.users || []);
      setSupporters(usersRes.data.supporters || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.content) {
      toast.error("Please fill in subject and message");
      return;
    }

    setSending(true);
    try {
      const payload = {
        subject: formData.subject,
        content: formData.content,
        recipient_type: formData.recipient_type,
        recipient_id: formData.recipient_type === "specific" ? formData.recipient_id : null
      };

      await axios.post(`${API}/messages`, payload, { withCredentials: true });
      toast.success("Message sent successfully!");
      setDialogOpen(false);
      setFormData({
        subject: "",
        content: "",
        recipient_type: "all",
        recipient_id: ""
      });
      fetchData();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getRecipientLabel = (message) => {
    if (message.is_broadcast) {
      return "All Users";
    }
    if (message.recipient_id) {
      const user = users.find(u => u.user_id === message.recipient_id);
      return user?.name || message.recipient_id;
    }
    return message.recipient_type;
  };

  return (
    <AdminLayout currentPage="Messages">
      <div className="p-8" data-testid="admin-messages">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Messages</h1>
            <p className="text-slate-600">Send announcements and communicate with supporters</p>
          </div>
          <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="compose-btn">
            <Send className="w-4 h-4 mr-2" />
            Compose Message
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-awareness-blue" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Messages</p>
                <p className="text-2xl font-bold text-deep-blue">{messages.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Registered Users</p>
                <p className="text-2xl font-bold text-deep-blue">{users.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Supporters</p>
                <p className="text-2xl font-bold text-deep-blue">{supporters.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-24 animate-pulse"></div>
            ))}
          </div>
        ) : messages.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Subject</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Recipient</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {messages.map(message => (
                  <tr key={message.message_id} data-testid={`message-row-${message.message_id}`}>
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-800">{message.subject}</p>
                        <p className="text-sm text-slate-500 line-clamp-1 mt-1">{message.content}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        message.is_broadcast 
                          ? 'bg-awareness-blue/10 text-awareness-blue' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {message.is_broadcast && <Megaphone className="w-3 h-3 inline mr-1" />}
                        {getRecipientLabel(message)}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">
                      {format(new Date(message.created_at), "MMM d, yyyy h:mm a")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No messages sent yet</h3>
            <p className="text-slate-500 mb-4">Start communicating with your supporters</p>
            <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
              <Send className="w-4 h-4 mr-2" />
              Compose Message
            </Button>
          </div>
        )}

        {/* Compose Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Compose Message</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSend} className="space-y-6">
              <div>
                <Label htmlFor="recipient_type">Send To</Label>
                <Select 
                  value={formData.recipient_type} 
                  onValueChange={(v) => setFormData({...formData, recipient_type: v, recipient_id: ""})}
                >
                  <SelectTrigger data-testid="recipient-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">
                        <Megaphone className="w-4 h-4" />
                        All Users (Broadcast)
                      </span>
                    </SelectItem>
                    <SelectItem value="specific">
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Specific User
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.recipient_type === "specific" && (
                <div>
                  <Label htmlFor="recipient_id">Select User</Label>
                  <Select 
                    value={formData.recipient_id} 
                    onValueChange={(v) => setFormData({...formData, recipient_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input 
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Message subject"
                  required
                  data-testid="message-subject"
                />
              </div>

              <div>
                <Label htmlFor="content">Message *</Label>
                <Textarea 
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Write your message here..."
                  rows={6}
                  required
                  data-testid="message-content"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={sending} data-testid="send-message-btn">
                  {sending ? "Sending..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
